import { Controller } from "@hotwired/stimulus";
import { effect, signal, untracked } from "@preact/signals-core";
import { csrfToken } from "../dice/roll_and_post";
import {
	type SendSession,
	type SoundEffect,
	type SoundGroup,
	type SoundLabels,
	soundBridge,
	type TableState,
	type Upload,
	type Uploads,
} from "../maps/sound_bridge";
import { createTableMusic, type MusicMessage, type TableMusic } from "../maps/table_music";
import { urlOf } from "../maps/table_sounds";
import type MapBoard from "./map_board_controller";
import { closeOtherPanels } from "./map_panels";

const VOLUME_DELAY = 200;
// The server's level for a table that has never set one (TableMusic::DEFAULT_VOLUME).
const DEFAULT_VOLUME = 60;
// The events a browser takes for a gesture that may start sound: a touch counts on pointerup, a mouse on pointerdown.
const GESTURES = ["pointerdown", "pointerup", "keydown"];

// How long the ids this board sent are remembered, to tell its own effects from the cable's echo.
const ID_TTL = 30_000;

type Write = { track: string | null; volume: number };
type EffectMessage = { type: "effect"; key: string; id: string | null; url?: string };

async function put(url: string, body: Write) {
	try {
		const response = await fetch(url, {
			method: "PUT",
			credentials: "same-origin",
			headers: { Accept: "application/json", "Content-Type": "application/json", "X-CSRF-Token": csrfToken() },
			body: JSON.stringify(body),
		});
		return response.ok;
	} catch {
		return false;
	}
}

type Change = { ok: boolean; row?: Upload; error?: string };

// A rename or a delete of the owner's own file; a refusal carries the server's words.
async function change(url: string, method: "PATCH" | "DELETE", body?: { name: string }): Promise<Change> {
	try {
		const response = await fetch(url, {
			method,
			credentials: "same-origin",
			headers: { Accept: "application/json", "Content-Type": "application/json", "X-CSRF-Token": csrfToken() },
			body: body && JSON.stringify(body),
		});
		if (response.ok) return { ok: true, row: method === "PATCH" ? ((await response.json().catch(() => undefined)) as Upload | undefined) : undefined };
		return { ok: false, error: ((await response.json().catch(() => ({}))) as { error?: string })?.error };
	} catch {
		return { ok: false };
	}
}

// Connects to data-controller="map-sound" on every board root, the master's, the players', the table view's and the link's: it owns the
// table's music player and hears the `music` messages the board relays. The master's board also owns the Som button and its panel,
// whose body is the `map_sound` island; every board's `map_sound_chip` island reads the player through `soundBridge`
// (docs/architecture/maps.md, "The table's music").
export default class extends Controller {
	static targets = ["panel", "toggle"];
	static values = { library: Array, labels: Object, url: String, manage: Boolean, effects: Array, effectUrl: String, uploads: Object };
	declare readonly panelTarget: HTMLElement;
	declare readonly hasPanelTarget: boolean;
	declare readonly toggleTarget: HTMLElement;
	declare readonly hasToggleTarget: boolean;
	declare readonly libraryValue: SoundGroup[];
	declare readonly labelsValue: SoundLabels;
	declare readonly urlValue: string;
	declare readonly manageValue: boolean;
	declare readonly effectsValue: SoundEffect[];
	declare readonly effectUrlValue: string;
	declare readonly uploadsValue: Uploads;
	declare readonly hasUploadsValue: boolean;
	readonly shown = signal(false);
	readonly table = signal<TableState>({ track: null, volume: DEFAULT_VOLUME });
	readonly uploads = signal(new Map<string, Upload>());
	readonly files = signal<Uploads | null>(null);
	// Counts the list's local changes, the panel's reads, the renames, and the renames and deletes still travelling, to tell an answer
	// that has been overtaken; `confirmed` is the name the server last accepted for a row while a rename of it is in flight.
	revision = 0;
	refresh = 0;
	edits = 0;
	inflight = 0;
	renames = new Map<string, number>();
	confirmed = new Map<string, string>();
	// Numbers this board's own plays, stops and level moves, so a failed delete does not restore over one made since.
	intent = 0;
	music!: TableMusic;
	// The last message the server sent, and the last one applied here, which the master's own tap may have written first.
	server: { message: MusicMessage; at: number } | null = null;
	applied: { message: MusicMessage; at: number } | null = null;
	seq = 0;
	sentIds = new Map<string, number>();
	queue: Promise<unknown> = Promise.resolve();
	// The newest slider level that no write has settled, which a message from the cable must not undo; `edit` numbers the slider's moves.
	pending: { volume: number; id: number } | null = null;
	edit = 0;
	volumeTimer?: ReturnType<typeof setTimeout>;
	disposeAccess?: () => void;
	send: SendSession = { draft: signal(null), abort: null, pick: 0 };

	connect() {
		const seconds = new Map(this.libraryValue.flatMap((group) => group.sounds).map((sound) => [sound.key, sound.seconds]));
		this.music = createTableMusic({
			resolve: (key) => {
				const url = urlOf(key);
				if (url) return { url, seconds: seconds.get(key) };
				const upload = this.uploads.value.get(key);
				return upload ? { url: upload.url, seconds: upload.seconds ?? undefined } : undefined;
			},
		});
		if (this.hasUploadsValue) {
			this.files.value = this.uploadsValue;
			this.learn(this.uploadsValue.rows);
		}
		for (const type of GESTURES) document.addEventListener(type, this.gesture, true);
		// Turbo moving the permanent root reconnects this instance, and the cable will not repeat what it already sent.
		if (this.server) this.hear(this.project(this.server));
		soundBridge.value = {
			labels: this.labelsValue,
			groups: this.libraryValue,
			effects: this.effectsValue,
			uploads: this.uploads,
			state: this.music.state,
			unlock: () => this.music.unlock(true),
			setVolume: (volume) => this.music.setVolume(volume),
			setOff: (off) => this.music.setOff(off),
			master: this.manageValue
				? {
						table: this.table,
						play: (track) => this.play(track),
						fire: (key) => void this.fire(key),
						setTableVolume: (volume) => this.setTableVolume(volume),
						close: () => this.close(),
						files: this.files,
						refreshFiles: () => this.refreshFiles(),
						rename: (id, name) => this.rename(id, name),
						remove: (id) => this.remove(id),
						send: this.send,
						add: (row) => this.mutate((rows) => [row, ...rows.filter((old) => old.id !== row.id)]),
					}
				: undefined,
		};
		this.watch();
	}

	disconnect() {
		this.close();
		// A send in flight or a file being read must not report to a controller that is gone, nor leave its form to the next board.
		const { send } = this;
		send.pick++;
		send.abort?.();
		send.abort = null;
		send.draft.value = null;
		clearTimeout(this.volumeTimer);
		this.disposeAccess?.();
		this.pending = null;
		for (const type of GESTURES) document.removeEventListener(type, this.gesture, true);
		this.music.destroy();
		soundBridge.value = null;
	}

	get board() {
		return this.application.getControllerForElementAndIdentifier(this.element, "map-board") as MapBoard | null;
	}

	// The board may boot after this controller connects (`ready` then), or already be running when Turbo moves the root back (`connect`).
	ready() {
		this.watch();
	}

	// A board that lost its access (a revoked table link or view, or a player taken off the campaign) plays nothing more.
	watch() {
		this.disposeAccess?.();
		const sync = this.board?.sync;
		if (!sync) return;
		this.disposeAccess = effect(() => {
			if (sync.status.value === "rejected") untracked(() => this.revoke());
		});
	}

	// The prompt's own buttons answer for themselves, Continuar sem som being no wish to hear, and Tab is on its way to them.
	gesture = (event: Event) => {
		if ((event as KeyboardEvent).key === "Tab" || (event.target as Element | null)?.closest?.("[data-sound-prompt]")) return;
		this.music.unlock();
	};

	learn(rows: Upload[]) {
		const known = new Map(this.uploads.value);
		let changed = false;
		for (const row of rows) {
			const old = known.get(row.id);
			if (old && old.url === row.url && old.name === row.name && old.seconds === row.seconds) continue;
			known.set(row.id, row);
			changed = true;
		}
		if (changed) this.uploads.value = known;
	}

	setRows(rows: Upload[]) {
		const files = this.files.value;
		if (files) this.files.value = { ...files, rows };
		this.learn(rows);
	}

	// Every local change to the list goes through here, so a read that began before it can tell it is out of date.
	mutate(change: (rows: Upload[]) => Upload[]) {
		this.revision++;
		this.setRows(change(this.files.value?.rows ?? []));
	}

	// Each time the panel opens: a mestre auxiliar sees what the owner sent since this board loaded.
	async refreshFiles() {
		const files = this.files.value;
		if (!files) return;
		const mine = ++this.refresh;
		const revision = this.revision;
		const busy = this.inflight > 0;
		try {
			const response = await fetch(files.url, { credentials: "same-origin", headers: { Accept: "application/json" } });
			if (!response.ok) return;
			const rows = (await response.json()) as Upload[];
			// A newer read, a change made while this one travelled, or a rename or delete not yet settled makes its answer old news.
			if (mine === this.refresh && revision === this.revision && !busy && this.inflight === 0) this.setRows(rows);
		} catch {
			// The list on screen stays.
		}
	}

	// Optimistic: the row shows the new name at once and goes back to the one the server confirmed on a refusal. Only the row's latest request may answer.
	async rename(id: string, name: string) {
		const files = this.files.value;
		const old = files?.rows.find((row) => row.id === id);
		if (!files || !old) return;
		const mine = ++this.edits;
		if (!this.renames.has(id)) this.confirmed.set(id, old.name);
		this.renames.set(id, mine);
		this.mutate((rows) => rows.map((row) => (row.id === id ? { ...row, name } : row)));
		this.inflight++;
		try {
			const result = await change(`${files.url}/${id}`, "PATCH", { name });
			if (this.renames.get(id) !== mine) {
				if (result.ok && this.renames.has(id)) this.confirmed.set(id, result.row?.name ?? name);
				return;
			}
			const restore = this.confirmed.get(id) ?? old.name;
			this.renames.delete(id);
			this.confirmed.delete(id);
			if (result.ok) {
				const saved = result.row;
				if (saved) this.mutate((rows) => rows.map((row) => (row.id === id ? saved : row)));
				return;
			}
			this.mutate((rows) => rows.map((row) => (row.id === id && row.name === name ? { ...row, name: restore } : row)));
			this.board?.toast(result.error ?? this.labelsValue.failed);
		} finally {
			this.inflight--;
		}
	}

	// The server's delete already silences every table, so this board only stops its own player, with no write.
	async remove(id: string) {
		const files = this.files.value;
		const index = files?.rows.findIndex((row) => row.id === id) ?? -1;
		if (!files || index < 0) return;
		const row = files.rows[index];
		const silenced = this.table.value.track === id;
		this.renames.delete(id);
		this.confirmed.delete(id);
		this.mutate((rows) => rows.filter((old) => old.id !== id));
		const stopped = silenced ? ++this.intent : 0;
		if (silenced) this.hear({ type: "music", track: null, started_at: null, now: Date.now(), volume: this.table.value.volume });
		this.inflight++;
		try {
			const result = await change(`${files.url}/${id}`, "DELETE");
			if (result.ok) return;
			this.mutate((rows) => (rows.some((old) => old.id === id) ? rows : rows.toSpliced(Math.min(index, rows.length), 0, row)));
			// Nothing played, stopped or moved since the stop: otherwise the table has moved on and this must not undo it.
			if (silenced && stopped === this.intent && this.table.value.track === null && this.server) this.hear(this.project(this.server));
			this.board?.toast(result.error ?? this.labelsValue.failed);
		} finally {
			this.inflight--;
		}
	}

	received(event: CustomEvent<MusicMessage>) {
		const { track, url, seconds, name } = event.detail;
		if (track && url) this.learn([{ id: track, kind: "music", name: name ?? "", seconds: seconds ?? null, url }]);
		this.server = { message: event.detail, at: Date.now() };
		this.hear(event.detail);
	}

	effected(event: CustomEvent<EffectMessage>) {
		const { key, id, url } = event.detail;
		if (url && !this.uploads.value.has(key)) this.learn([{ id: key, kind: "effect", name: "", seconds: null, url }]);
		if (id && this.sentIds.has(id)) return;
		this.music.effect(key);
	}

	// Sounds at once on the master's own board; the cable's copy of it, carrying this id, is not played again.
	async fire(key: string) {
		const id = `${Date.now().toString(36)}${Math.random().toString(36).slice(2)}`;
		const now = Date.now();
		for (const [old, at] of this.sentIds) if (now - at > ID_TTL) this.sentIds.delete(old);
		this.sentIds.set(id, now);
		this.music.effect(key);
		let error: string | undefined;
		try {
			const response = await fetch(this.effectUrlValue, {
				method: "POST",
				credentials: "same-origin",
				headers: { Accept: "application/json", "Content-Type": "application/json", "X-CSRF-Token": csrfToken() },
				body: JSON.stringify({ key, id }),
			});
			if (response.ok) return;
			// A refusal sends no echo; a lost or broken answer may still be followed by one, so its id stays.
			if (response.status >= 400 && response.status < 500) this.sentIds.delete(id);
			error = (await response.json().catch(() => ({})))?.error;
		} catch {
			// Offline: the toast below says so.
		}
		this.board?.toast(error ?? this.labelsValue.failed);
	}

	hear(message: MusicMessage) {
		if (this.pending) {
			if (message.track !== this.table.value.track) {
				this.pending = null;
				clearTimeout(this.volumeTimer);
			} else message = { ...message, volume: this.pending.volume };
		}
		this.take(message);
	}

	take(message: MusicMessage) {
		this.applied = { message, at: Date.now() };
		this.music.apply(message);
		this.table.value = { track: message.track, volume: message.volume };
	}

	// The message as it stands now: `now` is the server's clock as it sent it, so it moves on with the time since.
	project(held: { message: MusicMessage; at: number }, patch: Partial<MusicMessage> = {}): MusicMessage {
		return { ...held.message, ...patch, now: held.message.now + Date.now() - held.at };
	}

	// Sounds at once on the master's own board, from inside the click that browsers want for it, and the cable's message confirms it.
	play(track: string | null) {
		const { volume } = this.table.value;
		const now = Date.now();
		this.intent++;
		clearTimeout(this.volumeTimer);
		this.pending = null;
		this.hear({ type: "music", track, started_at: track ? now : null, now, volume });
		this.music.unlock();
		void this.write({ track, volume });
	}

	setTableVolume(level: number) {
		const volume = Math.round(Math.min(100, Math.max(0, level)));
		clearTimeout(this.volumeTimer);
		const now = Date.now();
		const held = this.applied ?? { message: { type: "music" as const, track: null, started_at: null, now, volume }, at: now };
		this.intent++;
		this.pending = { volume, id: ++this.edit };
		this.take(this.project(held, { volume }));
		this.volumeTimer = setTimeout(() => {
			const { pending, table } = this;
			if (pending) void this.write({ track: table.value.track, volume: pending.volume });
		}, VOLUME_DELAY);
	}

	// A refusal says so and goes back to what the server last sent, unless a later tap or slider move has since replaced this write.
	async write(body: Write) {
		const seq = ++this.seq;
		const edit = this.edit;
		// One request in flight at a time: a slow volume must not land after the stop that followed it.
		const sent = this.queue.then(() => put(this.urlValue, body));
		this.queue = sent;
		const ok = await sent;
		if (this.pending?.id === edit) this.pending = null;
		if (ok || seq !== this.seq || edit !== this.edit) return;
		this.board?.toast(this.labelsValue.failed);
		if (this.server) this.hear(this.project(this.server));
		else this.hear({ type: "music", track: null, started_at: null, now: Date.now(), volume: this.table.value.volume });
	}

	// The board lost its access: nothing more plays, and nothing held can bring it back.
	revoke() {
		clearTimeout(this.volumeTimer);
		this.pending = null;
		this.take({ type: "music", track: null, started_at: null, now: Date.now(), volume: this.table.value.volume });
		this.music.stopEffect();
		this.server = this.applied = null;
	}

	toggle() {
		if (this.shown.value) this.close();
		else this.show();
	}

	show() {
		closeOtherPanels(this.application, this.element, "map-sound");
		this.panelTarget.hidden = false;
		this.shown.value = true;
		void this.refreshFiles();
		if (this.hasToggleTarget) this.toggleTarget.setAttribute("aria-pressed", "true");
		this.panelTarget.focus({ preventScroll: true });
	}

	escape(event: KeyboardEvent) {
		if (event.defaultPrevented) return;
		// Stopped here, or the board's own Escape would also deselect behind the panel.
		event.preventDefault();
		event.stopPropagation();
		this.close();
	}

	close() {
		if (!this.shown.value) return;
		// Read before hiding: a hidden element gives its focus up at once.
		const focused = this.panelTarget.contains(document.activeElement);
		this.panelTarget.hidden = true;
		this.shown.value = false;
		if (this.hasToggleTarget) this.toggleTarget.setAttribute("aria-pressed", "false");
		if (focused) (this.hasToggleTarget ? this.toggleTarget : this.board?.canvasTarget)?.focus({ preventScroll: true });
	}
}
